/**
 * SAT · Block 6 · Wiederverwendbare Salespage-Sektionen
 * Design: Midnight (#0B1220), Gold (#C5A46A), Elfenbein (#F6F1E7)
 * Ruhe, Grosszügigkeit, klare Hierarchie · keine Gamification · keine Rabattoptik.
 */
import React from "react";

export const S = {
  midnight: "#0B1220",
  midnightSoft: "#141C2E",
  gold: "#C5A46A",
  goldSoft: "#D8BE8A",
  ivory: "#F6F1E7",
  ivoryMuted: "#EAE3D3",
  line: "rgba(197,164,106,0.28)",
};

export function Section({ id, children, className = "", eyebrow, title, intro }) {
  return (
    <section
      id={id}
      className={`w-full py-20 md:py-28 ${className}`}
      style={{ background: S.midnight, color: S.ivory }}
    >
      <div className="max-w-5xl mx-auto px-6 md:px-8">
        {eyebrow && (
          <p
            className="uppercase tracking-[0.22em] text-xs mb-4"
            style={{ color: S.goldSoft }}
            data-testid={`section-eyebrow-${id}`}
          >
            {eyebrow}
          </p>
        )}
        {title && (
          <h2
            className="text-3xl md:text-5xl font-serif leading-tight mb-6"
            style={{ color: S.ivory }}
            data-testid={`section-title-${id}`}
          >
            {title}
          </h2>
        )}
        {intro && (
          <p
            className="text-base md:text-lg leading-relaxed max-w-3xl mb-8"
            style={{ color: S.ivoryMuted }}
            data-testid={`section-intro-${id}`}
          >
            {intro}
          </p>
        )}
        {children}
      </div>
    </section>
  );
}

export function GoldRule() {
  return (
    <div
      className="h-px my-10"
      style={{ background: `linear-gradient(90deg, transparent, ${S.gold}, transparent)` }}
    />
  );
}

export function PrimaryCta({ href, label, testId }) {
  return (
    <a
      href={href}
      data-testid={testId}
      className="inline-block px-8 py-4 text-sm md:text-base tracking-wider uppercase transition-all duration-500"
      style={{
        background: S.gold,
        color: S.midnight,
        fontWeight: 500,
        letterSpacing: "0.16em",
        border: `1px solid ${S.gold}`,
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color = S.gold;
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.background = S.gold;
        e.currentTarget.style.color = S.midnight;
      }}
    >
      {label}
    </a>
  );
}

export function TwoColumn({ left, right }) {
  return (
    <div className="grid md:grid-cols-2 gap-10 items-start">
      <div>{left}</div>
      <div>{right}</div>
    </div>
  );
}

export function ChecklistList({ items, testIdPrefix = "list" }) {
  return (
    <ul className="space-y-3" data-testid={`${testIdPrefix}-checklist`}>
      {items.map((it) => (
        <li
          key={it}
          className="flex items-start gap-3"
          style={{ color: S.ivoryMuted }}
        >
          <span
            style={{
              color: S.gold,
              lineHeight: "1.6",
              flexShrink: 0,
            }}
            aria-hidden
          >
            ◈
          </span>
          <span className="text-base leading-relaxed">{it}</span>
        </li>
      ))}
    </ul>
  );
}

export function Accordion({ items, testIdPrefix }) {
  const [open, setOpen] = React.useState(-1);
  return (
    <div
      className="rounded-none divide-y"
      style={{ borderColor: S.line, borderTop: `1px solid ${S.line}`, borderBottom: `1px solid ${S.line}` }}
    >
      {items.map((it, idx) => {
        const isOpen = open === idx;
        return (
          <div
            key={it.title}
            style={{ borderColor: S.line }}
            className="py-2"
            data-testid={`${testIdPrefix}-item-${idx}`}
          >
            <button
              type="button"
              onClick={() => setOpen(isOpen ? -1 : idx)}
              className="w-full flex items-center justify-between py-5 text-left transition-colors"
              style={{ color: S.ivory }}
              data-testid={`${testIdPrefix}-toggle-${idx}`}
              aria-expanded={isOpen}
            >
              <span className="text-base md:text-lg font-serif tracking-wide">
                {it.title}
              </span>
              <span
                className="text-sm transition-transform"
                style={{
                  color: S.gold,
                  transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                }}
                aria-hidden
              >
                +
              </span>
            </button>
            {isOpen && (
              <div className="pb-6 pr-8">
                <div
                  className="text-sm md:text-base leading-relaxed"
                  style={{ color: S.ivoryMuted }}
                >
                  {it.body}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function FooterDisclaimer() {
  return (
    <div
      className="text-xs leading-relaxed max-w-3xl mx-auto text-center px-6 py-10"
      style={{ color: "rgba(246,241,231,0.55)" }}
      data-testid="footer-disclaimer"
    >
      SAT ist ein Transformations- und Bildungsangebot und ersetzt keine
      ärztliche, psychiatrische oder psychotherapeutische Behandlung. SAT
      gibt keine Heilungs-, Diagnose-, Therapie-, Einkommens- oder
      Erfolgsgarantien. Teilnahme an Übungen bleibt jederzeit freiwillig;
      jede Übung kann jederzeit unterbrochen werden.
    </div>
  );
}
